import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ["search", "sortLabel"]

  connect() {
    this.lastValue = this.searchTarget.value
    // Capture the fetch options of this form's Turbo submissions (the event
    // bubbles up from the form) so disconnect can abort an in-flight search.
    this.boundCaptureSearchRequest = this.captureSearchRequest.bind(this)
    this.element.addEventListener("turbo:before-fetch-request", this.boundCaptureSearchRequest)
  }

  disconnect() {
    clearTimeout(this.searchTimeout)
    this.element.removeEventListener("turbo:before-fetch-request", this.boundCaptureSearchRequest)
    // Turbo resolves a frame response's target by ID against the *current*
    // document, so a search response outliving this page (tab switch while
    // results load) would render the old rows into the new page's frame
    // (CORE-9041). Abort it instead; Turbo swallows AbortError silently.
    this.abortController?.abort()
  }

  // Swap in a signal we control. Turbo's own abort (superseding submission on
  // the same form) is composed in, or forwarded when AbortSignal.any is
  // unavailable, so at most one search is in flight per form either way.
  captureSearchRequest(event) {
    const fetchOptions = event.detail?.fetchOptions
    if (!fetchOptions) return

    // Local capture keeps the abort forwarding correlated to this request even
    // if another one is captured before the Turbo signal fires.
    const abortController = new AbortController()
    this.abortController = abortController
    const turboSignal = fetchOptions.signal
    if (turboSignal && typeof AbortSignal.any === "function") {
      fetchOptions.signal = AbortSignal.any([turboSignal, abortController.signal])
    } else {
      turboSignal?.addEventListener("abort", () => abortController.abort(), { once: true })
      fetchOptions.signal = abortController.signal
    }
  }

  // Submits 250ms after typing stops. The timeout lives on the instance so disconnect
  // can cancel a pending submit if the page navigates away mid-debounce.
  debouncedSearch() {
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => {
      if (this.lastValue !== this.searchTarget.value) {
        this.lastValue = this.searchTarget.value
        this.search()
      }
    }, 250)
  }

  search() {
    // Handle folders visibility
    var folders = document.querySelector(".index__folders");
    if (folders) {
      if (this.searchTarget.value) {
        folders.style.display = "none";
      } else {
        folders.style.display = "block";
      }
    }
    // Handle search
    const form = this.searchTarget.form
    this.syncHiddenFieldsFromUrl(form)
    form.requestSubmit()
  }

  // Search and chips sit outside the results turbo frame. A later chip
  // selection updates the URL, but this form still has the hidden fields from
  // the last full-page render. Copy live URL state so clearing search does not
  // wipe Role / label filters (or sort).
  syncHiddenFieldsFromUrl(form) {
    this.syncScalarParamsFromUrl(form)
    this.syncArrayParamFromUrl(form, "role_filter[values][]")
    this.syncArrayParamFromUrl(form, "label_filter[values][]")
  }

  syncArrayParamFromUrl(form, name) {
    if (!form) return

    const values = new URLSearchParams(window.location.search).getAll(name)
    form.querySelectorAll(`input[name="${name}"]`).forEach((input) => input.remove())
    values.forEach((value) => {
      const hidden = document.createElement("input")
      hidden.type = "hidden"
      hidden.name = name
      hidden.value = value
      form.appendChild(hidden)
    })
  }

  // Column sorts and the sync type / archived filters live inside the results
  // turbo frame and update the browser URL, but the search form sits outside that
  // frame and keeps its original hidden fields. Copy the live values from the URL
  // so a search does not reset them.
  syncScalarParamsFromUrl(form) {
    if (!form) return

    const urlParams = new URLSearchParams(window.location.search)
    const scalarParams = ["sort_column", "sort_direction", "asset_layout_field_id", "type", "archived"]

    scalarParams.forEach((name) => {
      const value = urlParams.get(name)
      const input = form.querySelector(`[name="${name}"]`)

      if (value) {
        if (input) {
          input.value = value
        } else {
          const hidden = document.createElement("input")
          hidden.type = "hidden"
          hidden.name = name
          hidden.value = value
          form.appendChild(hidden)
        }
      } else if (input) {
        input.remove()
      }
    })
  }

  selectSort(event) {
    const { sortColumn, sortDirection, label } = event.currentTarget.dataset

    if (label && this.hasSortLabelTarget) {
      this.sortLabelTarget.textContent = label
    }

    this.submitSort(event.currentTarget.closest("form"), sortColumn, sortDirection)
  }

  submitSort(form, sortColumn, sortDirection) {
    if (!form) return
    if (!sortColumn || !sortDirection) return

    const sortColumnInput = form.querySelector('[name="sort_column"]')
    const sortDirectionInput = form.querySelector('[name="sort_direction"]')

    if (sortColumnInput) sortColumnInput.value = sortColumn
    if (sortDirectionInput) sortDirectionInput.value = sortDirection

    form.requestSubmit()
  }

  // Places the caret at the end of the field on focus, such as when the input
  // keeps its value across a Turbo Frame reload, so typing continues from the end.
  moveCursorToEnd() {
    const length = this.searchTarget.value.length
    this.searchTarget.setSelectionRange(length, length)
  }

  preserveSearch(event) {
    const url = new URL(event.currentTarget.href, window.location.origin)
    const query = this.searchTarget.value.trim()

    if (query.length > 0) {
      url.searchParams.set("query", query)
    } else {
      url.searchParams.delete("query")
    }

    event.currentTarget.setAttribute("href", `${url.pathname}${url.search}${url.hash}`)
  }
}
